<template>
    <tab-layout>
        <template slot="content">
            <div class="ai-summary">
                <div class="ai-summary__header">
                    <img
                        src="/ressources/logged/img/illustrations/ai-double-sparkle.svg"
                        width="40"
                    />
                    <h2 class="ai-summary__title">{{ $t.__('overview.tabs.TabSummary') }}</h2>
                    <div class="ai-summary__header-actions">
                        <CompassLoader
                            v-if="getInProgress"
                            size="large"
                            class="ai-summary__loader"
                        />
                        <template v-else>
                            <div
                                v-if="!showFirstTimeContent || showRemainingQuota"
                                class="ai-summary__generation-information"
                            >
                                <div v-if="!showFirstTimeContent" class="ai-summary__timestamp">
                                    <CompassIcon icon="clock" size="small" />
                                    <p class="typographies-paragraph-medium-regular">
                                        {{
                                            $t.__('overview.tabs.TabSummary.generationDateTime', {
                                                date: formattedDate,
                                                time: formattedTime,
                                            })
                                        }}
                                    </p>
                                </div>
                                <div
                                    v-if="showRemainingQuota"
                                    class="ai-summary__remaining-quota"
                                    :class="remainingQuotaClass"
                                >
                                    <CompassFeatureBadge
                                        v-if="quotaAILimitReached"
                                        variant="upsell"
                                        size="extra-small"
                                    />
                                    <CompassIcon
                                        v-else
                                        icon="ai-icon-write"
                                        type="secondary"
                                        size="small"
                                    />
                                    <span class="typographies-paragraph-x-small-regular">
                                        {{ remainingQuotaLabel }}
                                    </span>
                                </div>
                            </div>
                            <CompassTooltip
                                :disabled="!disabledGenerationButton || quotaAILimitReached"
                                :content="translations.notEnoughData"
                                :arrow="false"
                            >
                                <ButtonAI
                                    ref="generateButton"
                                    :label="generateButtonLabel"
                                    :loading="isLoading"
                                    :disabled="disabledGenerationButton && !quotaAILimitReached"
                                    size="medium"
                                    type="primary-contrast"
                                    @click="handleGenerate"
                                />
                            </CompassTooltip>
                        </template>
                    </div>
                </div>

                <div v-show="getInProgress || isLoading" class="ai-summary__loading-animation">
                    <div ref="lottie" class="ai-summary__lottie" />
                    <div
                        v-show="isLoading"
                        ref="loadingMessageRef"
                        class="ai-summary__loading-animation-word-list"
                    >
                        {{ loadingMessage }}
                    </div>
                </div>

                <div v-if="!getInProgress && !isLoading" class="ai-summary__content-wrapper">
                    <div v-if="showFirstTimeContent" class="ai-summary__content">
                        <h3
                            class="typographies-display-brand-medium ai-summary__content-title"
                            v-html="translations.noData.title"
                        />
                        <p
                            class="ai-summary__content-description typographies-paragraph-medium-regular"
                        >
                            {{ translations.noData.description }}
                        </p>
                        <img
                            src="/ressources/logged/img/illustrations/ai-summary-illustration.svg"
                            width="774"
                        />
                    </div>
                    <div
                        v-if="!showFirstTimeContent && !isLoading"
                        class="ai-summary__content-grid"
                    >
                        <div class="ai-summary__content-column">
                            <transition appear name="slide-up">
                                <SummaryCard type="client" class="ai-summary__card" />
                            </transition>
                            <transition appear name="slide-up">
                                <SummaryCard type="document" class="ai-summary__card" />
                            </transition>
                            <transition appear name="slide-up">
                                <SummaryCard type="sales" class="ai-summary__card" />
                            </transition>
                        </div>
                        <div class="ai-summary__content-column">
                            <transition appear name="slide-up">
                                <SummaryCard
                                    type="interactions"
                                    class="ai-summary__card-interactions"
                                />
                            </transition>
                        </div>
                        <div class="ai-summary__content-column">
                            <transition appear name="slide-up">
                                <SummaryCard
                                    type="recommendations"
                                    class="ai-summary__card-recommendations"
                                />
                            </transition>
                        </div>
                    </div>
                </div>
                <div v-if="showSummary" class="ai-summary__footer">
                    <div class="ai-summary__feedback">
                        <CompassIcon icon="ai-icon-write" type="secondary" size="small" />
                        <p class="ai-summary__feedback-label typographies-paragraph-small-regular">
                            {{ translations.generated_by }}
                        </p>
                        <FeedbackPopover
                            :tags="tags"
                            placement="top-end"
                            :saveOnLike="true"
                            @tagSelected="handleTagSelect"
                            @saveFeedback="handleSaveFeedback"
                        />
                    </div>
                </div>
            </div>
        </template>
    </tab-layout>
</template>
<script>
import lottieWeb from 'lottie-web';
import animation from 'animations/lottie/loader.json';
import { createNamespacedHelpers } from 'vuex';
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';
import ButtonAI from 'vcomponents/common/ai/ButtonAI.vue';
import SummaryCard from 'vcomponents/AISummary/SummaryCard/SummaryCard.vue';
import { getSummary } from 'api/ai/summaryService';
import { generateSummary } from '../../../api/ai/summaryService';
import { searchActivities } from 'api/activities/activitiesService';
import FeedbackPopover from 'vcomponents/common/ai/FeedbackPopover/FeedbackPopover.vue';
import FeedbackService from 'api/ai/feedbackService';

const {
    mapState: corpMapState,
    mapGetters: corpMapGetters,
    mapActions: corpMapActions,
} = createNamespacedHelpers('corp');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        TabLayout,
        ButtonAI,
        SummaryCard,
        FeedbackPopover,
    },
    provide() {
        return {
            getSummary: () => this.summary,
        };
    },
    data() {
        return {
            getInProgress: true,
            isLoading: false,
            translations: {
                noData: {
                    title: this.$t.__('overview.tabs.TabSummary.first_time.title', {
                        br: '<br />',
                    }),
                    description: this.$t.__('overview.tabs.TabSummary.first_time.description'),
                },
                notEnoughData: this.$t.__('overview.tabs.TabSummary.not_enough_data'),
                generated_by: this.$t.__('overview.tabs.TabSummary.generated_by'),
            },
            remainingQuotaAI: null,
            loadingRemainingQuotaAI: false,
            summary: {},
            disabledGenerationButton: true,
            tags: [
                {
                    label: this.$t.__('overview.tabs.TabSummary.feedback_tags.not_adapted'),
                    selected: false,
                },
                {
                    label: this.$t.__('overview.tabs.TabSummary.feedback_tags.bad_informations'),
                    selected: false,
                },
                {
                    label: this.$t.__('overview.tabs.TabSummary.feedback_tags.incomplete'),
                    selected: false,
                },
                {
                    label: this.$t.__(
                        'overview.tabs.TabSummary.feedback_tags.irrelevant_recommendations',
                    ),
                    selected: false,
                },
            ],
            feedbackService: new FeedbackService(),
            loadingMessageFlag: 0,
            loadingMessage: this.$t.__('overview.tabs.TabSummary.loading_message_1'),
            loadingMessageList: [
                this.$t.__('overview.tabs.TabSummary.loading_message_1'),
                this.$t.__('overview.tabs.TabSummary.loading_message_2'),
                this.$t.__('overview.tabs.TabSummary.loading_message_3'),
            ],
        };
    },
    computed: {
        ...corpMapState(['timeformat', 'hourformat', 'plan', 'pricingVersion']),
        ...corpMapGetters(['getQuotaAI', 'quotaAILimitReached']),
        identity() {
            return this.$store.getters.identity;
        },
        showFirstTimeContent() {
            return Object.keys(this.summary).length === 0 || this.summary.status === 'none';
        },
        generateButtonLabel() {
            if (this.quotaAILimitReached) {
                return this.$t.__('ai.SellsyAIMenu.upsell_label');
            }

            return this.showFirstTimeContent
                ? this.$t.__('common.actions.generate')
                : this.$t.__('common.actions.refresh');
        },
        formattedDate() {
            return this.timeformat === 'dmy'
                ? moment(this.summary.updated_at).format('DD/MM/YYYY')
                : moment(this.summary.updated_at).format('MM/DD/YYYY');
        },
        formattedTime() {
            return this.hourformat === '24'
                ? moment(this.summary.updated_at).format('HH:mm')
                : moment(this.summary.updated_at).format('hh:mm a');
        },
        showRemainingQuota() {
            if (this.plan && this.pricingVersion === 5) {
                return !(this.plan.includes('ELITE') || this.plan.includes('EVOLUTION'));
            }
            return true;
        },
        remainingQuota() {
            if (!this.getQuotaAI) {
                return null;
            }
            return this.getQuotaAI?.limit - this.getQuotaAI?.used;
        },
        remainingQuotaLabel() {
            if (this.quotaAILimitReached) {
                return this.$t.__('ai.SellsyAIMenu.label_limit_quota');
            }

            return this.$t.n__(
                'ai.SellsyAIMenu.label_remaining_quota',
                null,
                this.remainingQuota,
                this.remainingQuota,
            );
        },
        remainingQuotaClass() {
            return !this.showFirstTimeContent && 'ai-summary__remaining-quota--has-divider';
        },
        showSummary() {
            return !this.getInProgress && !this.showFirstTimeContent && !this.isLoading;
        },
    },
    async created() {
        if (!this.identity.isCorporation || this.identity.type !== 'client') {
            this.$router.push({ name: 'third.overview.activity' });
            return;
        }
        await this.getActivities();
        await this.fetchQuotaAI();
        EventBus.$on('summary:failed', async (event) => {
            this.isLoading = false;
            if (parseInt(event.summary.linkedId) === parseInt(this.identity.id)) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__('overview.tabs.TabSummary.generation_error'),
                });
            }
        });
        EventBus.$on('summary:generated', async (event) => {
            const { linkedType, linkedId } = event.summary;

            if (linkedType !== 'third') {
                return;
            }
            if (!this.identity.isCorporation) {
                return;
            }
            if (this.identity.type !== 'client') {
                return;
            }
            if (parseInt(linkedId) !== parseInt(this.identity.id)) {
                return;
            }

            this.summary = event.summary;
            await this.fetchQuotaAI();
            this.isLoading = false;
        });
        getSummary(this.identity.id)
            .then((response) => {
                this.summary = response;
            })
            .catch((error) => {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: error,
                });
            })
            .finally(() => {
                this.getInProgress = false;
            });
    },
    mounted() {
        lottieWeb.loadAnimation({
            container: this.$refs.lottie, // the dom element that will contain the animation
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: animation, // the path to the animation json
        });
        this.$refs.loadingMessageRef.addEventListener('animationiteration', () => {
            this.loadingMessageFlag++;
            this.loadingMessage =
                this.loadingMessageList[this.loadingMessageFlag % this.loadingMessageList.length];
        });
    },
    methods: {
        ...corpMapActions(['fetchQuotaAI']),
        ...popupPanelMapActions(['open']),
        handleGenerate() {
            this.isLoading = true;
            if (this.quotaAILimitReached) {
                this.open({
                    key: 'featureModal',
                    props: {
                        feature: 'upsell-ai',
                        type: 'upsell-ai',
                    },
                });
                this.isLoading = false;
                return;
            }
            generateSummary(this.identity.id)
                .then()
                .catch((error) => {
                    this.isLoading = false;
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: error,
                    });
                });
        },
        async getActivities() {
            const recentActivities = await searchActivities(
                {
                    limit: 10,
                    field: ['event'],
                },
                {
                    object_related: {
                        type: 'company',
                        id: parseInt(this.identity.id),
                    },
                    entities: [
                        'email',
                        'calendar_event',
                        'task',
                        'comment',
                        'phonecall',
                        'opportunity',
                        'invoice',
                        'estimate',
                    ],
                    activity_level: ['main'],
                },
            );

            this.disabledGenerationButton = recentActivities.data.pagination.total < 5;
        },
        handleTagSelect(index) {
            this.tags = this.tags.map((tag, i) => ({
                ...tag,
                selected: i === index ? !tag.selected : false,
            }));
        },
        handleSaveFeedback({ messageFeedback, isLiked = false, isDisliked = false }) {
            if (!isLiked && !isDisliked) {
                return;
            }
            const uuid = this.summary.last_request_id;
            const params = {
                uuid,
                output: this.summary.summary,
                reaction: isLiked ? 'like' : 'dislike',
                type: 'summary',
            };
            if (isDisliked) {
                params.reason = this.tags.find((tag) => tag.selected)?.label || null;
                params.feedback = messageFeedback.slice(0, 999) ?? null;
            }
            this.feedbackService.create(params);
        },
    },
};
</script>
<style lang="scss" scoped>
.ai-summary {
    &__header {
        align-items: center;
        background: var(--colors-sellsy-ai-gradient);
        border-radius: var(--spacing-1);
        display: flex;
        gap: var(--spacing-1);
        margin: 0 0 var(--spacing-3);
        padding: var(--spacing-3) var(--spacing-4);

        &-actions {
            align-items: center;
            display: flex;
            gap: var(--spacing-3);
            margin-left: auto;
        }
    }

    &__generation-information {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);
    }

    &__remaining-quota {
        align-items: center;
        color: var(--colors-common-text-contrast-caption);
        display: flex;
        gap: var(--spacing-0);

        &--has-divider {
            border-left: 1px solid var(--colors-common-border-mask);
            padding-left: calc(var(--spacing-1) - 1px);
        }

        &::v-deep {
            .compass-feature-badge {
                flex-shrink: 0;
            }

            > .compass-icon {
                color: var(--colors-common-text-standard-on-color);
                flex-shrink: 0;
                height: 14px;
                width: 14px;
            }
        }
    }

    &__loading-animation {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 160px 0;

        &-word-list {
            animation: fade-in-down 3s ease-in-out infinite;
            color: var(--colors-sellsy-ai-20);
            font-family: 'Patron', sans-serif;
            font-size: 17px;
            font-weight: 500;
            line-height: 1.2;
            padding: 20px 0;
        }
    }

    &__lottie {
        width: 69px;
    }

    &__loader {
        color: var(--colors-common-text-standard-on-color);
    }

    &__timestamp {
        align-items: center;
        color: var(--colors-common-text-standard-on-color);
        display: flex;
        gap: var(--spacing-1);

        p {
            margin: 0;
        }
    }

    &__title {
        color: var(--colors-common-text-standard-on-color);
        margin: 0;
    }

    &__content {
        align-items: center;
        display: flex;
        flex-direction: column;
        padding: 30px 0 0;
        text-align: center;

        &-wrapper {
            margin: 0 auto;
            max-width: 1400px;
        }

        &-title {
            color: var(--colors-common-text-brand-secondary-blue);
            margin: 0 0 var(--spacing-3);
            max-width: 600px;
        }

        &-description {
            color: var(--colors-common-text-brand-secondary-blue);
            margin: 0;
            max-width: 600px;
        }

        img {
            display: block;
            height: auto;
            max-width: 100%;
            margin-top: var(--spacing-5);
        }

        &-grid {
            display: flex;
            flex-wrap: wrap;
            gap: var(--spacing-3);
        }

        &-column {
            display: flex;
            flex-direction: column;
            flex: 1 0 300px;
            gap: var(--spacing-3);
        }
    }

    &__footer {
        border-top: 1px solid var(--colors-common-border-lighter);
        display: flex;
        justify-content: center;
        margin: var(--spacing-3) 0 0;
        padding: 15px 0 0;
    }

    &__feedback {
        align-items: center;
        color: var(--colors-sellsy-grayscale-80);
        display: flex;
        gap: var(--spacing-0);
        padding: var(--spacing-0) var(--spacing-1);

        &-label {
            margin: 0 var(--spacing-0) 0 0;
        }
    }
}

.slide-up-enter-active {
    animation: slide-up 400ms;
}
@keyframes slide-up {
    0% {
        opacity: 0;
        transform: translate3d(0, 10%, 0);
    }
    100% {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

@keyframes fade-in-down {
    0% {
        opacity: 0;
        transform: translateY(-20px);
    }
    33% {
        opacity: 1;
        transform: translateY(0);
    }
    66% {
        opacity: 1;
        transform: translateY(0);
    }
    100% {
        opacity: 0;
        transform: translateY(20px);
    }
}
</style>
